import { makeAutoObservable, runInAction } from 'mobx';
import type { ResponseDataRow } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { cmApi } from '@src/api';
import { preloaderStore } from '@stores/preloaderStore';
import type { CmApi, ControlApplicabilityDto } from '@src/api';

export type ControlApplicabilityDtoDefId = Omit<ControlApplicabilityDto, 'id'> & {
  id: string;
};

export type Filters = {
  reportGroup?: string;
  organization?: string;
};

class ControlApplicabilityStore {
  controlApplicability: ControlApplicabilityDtoDefId[] = [];

  api: CmApi;

  filters: Filters = {};

  breadcrumbs: string[] = [];

  fullSize = false;

  selectNode: ControlApplicabilityDtoDefId | null = null;

  selectedKeys: string[] = [];

  expandedKeys: string[] = [];

  checked: ResponseDataRow[] = [];

  isChanged = false; // флаг, что справочник изменился для проверки загрузки в fetchReferences

  templateModalOpen = false;

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    makeAutoObservable(this);
  }

  setIsChanged = (value: boolean) => {
    this.isChanged = value;
  };

  setFilters = (filter: Filters) => {
    this.filters = filter;
  };

  setBreadcrumbs = (value: string[]) => {
    this.breadcrumbs = value;
  };

  toggleFullSize = () => {
    this.fullSize = !this.fullSize;
  };

  setSelectNode = (value: ControlApplicabilityDtoDefId | null) => {
    this.selectNode = value;

    if (value === null) {
      this.fullSize = false;
    }
  };

  setSelectedKeys = (value: string[]) => {
    this.selectedKeys = value;
  };

  setExpandedKeys = (value: string[]) => {
    this.expandedKeys = value;
  };

  setChecked = (value) => {
    this.checked = value;
  };

  toggleTemplateModal = () => {
    this.templateModalOpen = !this.templateModalOpen;
  };

  fetch = async (): Promise<void> => {
    preloaderStore.preloader(true);
    const { data } = await this.api.controlApplicability.getControlApplicabilities();

    runInAction(() => {
      this.controlApplicability =
        data?.map((item) => ({ ...item, id: `${item.orgCode}-${item.repGroupCode}-${item.ctrlVersCode}` })) ?? [];
      this.isChanged = false;
    });
    preloaderStore.preloader(false);
  };

  groupDelete = async (controls: ControlApplicabilityDtoDefId[]) => {
    preloaderStore.preloader(true, 'удаление...');
    const promises = controls.map((item) =>
      this.api.controlApplicability.deleteControlApplicabilityByCodes(item.ctrlVersCode ?? '', item.orgCode ?? ''),
    );
    await Promise.all(promises);
    await this.fetch();
    preloaderStore.preloader(false);
  };

  clear = () => {
    this.filters = {};
    this.breadcrumbs = [];
    this.fullSize = false;
    this.selectNode = null;
    this.selectedKeys = [];
    this.expandedKeys = [];
    this.checked = [];
    this.templateModalOpen = false;
  };
}

export const controlApplicabilityStore = new ControlApplicabilityStore({ api: cmApi });
